/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { Drawer } from "@duxton-web/ui"
import {
  BULK_UPLOAD_TYPE,
  BulkUploadContext,
  DRAWER_STATES,
} from "pages/menu/detail/bulkUpload/BulkUploadContext"
import { useContext, useEffect } from "react"
import { useTranslations } from "common/utils/i18n"
import { useScribeUploadAndPublishDrawer } from "pages/menu/detail/bulkUpload/scribe/useScribeUploadAndPublishDrawer"
import { ACTION_TYPE } from "types/delv-platform-api/inventoryCategory"
import DrawerDownloadCatalogueComponent from "./drawerDownloadCatalogue/DrawerDownloadCatalogue"
import DrawerStartAndPublish from "./drawerStartAndPublish/DrawerStartAndPublish"
import { useHidingDrawerCompletedTask } from "./hooks/useHidingDrawerCompletedTask"

const drawerWithoutHeaderCss = css`
  .dui-drawer-header {
    display: none;
  }
`

const drawerUnclosableCss = css`
  .dui-drawer-close {
    pointer-events: none;
    opacity: 0.3;
  }
`

const BulkUploadDrawer = () => {
  useHidingDrawerCompletedTask()
  const {
    bulkUploadType,
    drawerState,
    drawerVisible,
    setDrawerVisible,
    actionType,
  } = useContext(BulkUploadContext)
  const { trackPageView, trackExitClick } = useScribeUploadAndPublishDrawer()

  const [
    editInventoryTitleText,
    CreateItemTitleText,
    editItemTitleText,
  ] = useTranslations([
    "bulkUpload.drawers.start.title-edit-inventory",
    "bulkUpload.drawers.start.title-create-item",
    "bulkUpload.drawers.start.title-edit-item",
  ])

  const renderTitle = () => {
    if (bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY)
      return editInventoryTitleText
    if (actionType === ACTION_TYPE.CREATE) return CreateItemTitleText
    return editItemTitleText
  }

  const showDownloadCatalogueDrawer =
    drawerState === DRAWER_STATES.DOWNLOAD_CATALOGUE
  // dont allow to close drawer during publishing, but don't prevent user to reload page
  const drawerClosable =
    drawerState !== DRAWER_STATES.PUBLISHING &&
    drawerState !== DRAWER_STATES.PUBLISHING_ERROR_CHECK

  useEffect(() => {
    if (drawerVisible) trackPageView()
  }, [drawerVisible, trackPageView])

  const renderDrawer = () => {
    if (showDownloadCatalogueDrawer) return <DrawerDownloadCatalogueComponent />
    return <DrawerStartAndPublish />
  }

  return (
    <Drawer
      css={css`
        ${showDownloadCatalogueDrawer ? drawerWithoutHeaderCss : ""}
        ${!drawerClosable ? drawerUnclosableCss : ""}
      `}
      width={480}
      open={drawerVisible}
      title={renderTitle()}
      onClose={() => {
        setDrawerVisible(false)
        trackExitClick()
      }}
      maskClosable={false}
      destroyOnClose // destroy to reset all drawer states and also selected file
      keyboard={false} // dont suppose to close the drawer by pressing ESC
    >
      {renderDrawer()}
    </Drawer>
  )
}

export default BulkUploadDrawer
